import React, { useCallback, useEffect, useRef } from 'react';
import './Modal.css';

/**
 * A dialog, for work that wants room.
 *
 * Built because the contractor's assignment card had grown to hold a
 * description, a status control, a timesheet, an expenses panel, the files, the
 * custom fields and a comment thread - four of those being forms - all inside a
 * 350px grid cell, half of it behind a "Show Details" button. Gordon asked for
 * the rows to collapse to the facts and for the work to happen in a popup, and
 * he is right: editing in a column that narrow is the reason the card kept
 * being folded.
 *
 * Deliberately not a library. What a dialog has to get right is small and
 * specific, and each of these is a real failure rather than polish:
 *
 *   ESCAPE CLOSES IT, and the listener is removed on unmount - otherwise every
 *   dialog ever opened keeps listening and the last one to be opened is not
 *   the one that closes.
 *
 *   THE BACKDROP CLOSES IT, but only when the press started on the backdrop.
 *   Checking the target of a click alone closes the dialog when somebody
 *   selects text inside it and releases outside, which loses whatever they
 *   had typed.
 *
 *   THE PAGE BEHIND DOES NOT SCROLL, restored exactly as it was rather than
 *   set to a hardcoded value, because a page that was already overflow-hidden
 *   must not be left scrollable afterwards.
 *
 *   FOCUS MOVES IN and goes back where it came from on close, so a keyboard
 *   user is not returned to the top of the document.
 *
 *   role="dialog" with aria-modal and a labelled title, so a screen reader
 *   announces it as a dialog rather than reading the page it covers.
 */
const Modal = ({ title, subtitle, onClose, children, footer, wide = false }) => {
  const panel = useRef(null);
  const backdropPress = useRef(false);
  const returnTo = useRef(null);

  const close = useCallback(() => { if (onClose) onClose(); }, [onClose]);

  useEffect(() => {
    returnTo.current = document.activeElement;
    /*
     * FOCUS STAYS INSIDE. aria-modal tells a screen reader the page behind
     * is inert; it does not tell the keyboard, and Tab walked out of the
     * dialog into the page under it. So Tab from the last control wraps to
     * the first and Shift+Tab from the first to the last - and from the
     * panel itself (where focus lands on open) Tab goes to the first.
     */
    const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), '
      + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const trap = (e) => {
      const root = panel.current;
      if (!root) return;
      const items = Array.from(root.querySelectorAll(TABBABLE))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (items.length === 0) { e.preventDefault(); root.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const at = document.activeElement;
      const inside = root.contains(at);
      if (e.shiftKey) {
        if (!inside || at === first || at === root) { e.preventDefault(); last.focus(); }
      } else if (!inside || at === last || at === root) {
        e.preventDefault(); first.focus();
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
      if (e.key === 'Tab') trap(e);
    };
    document.addEventListener('keydown', onKey);

    const had = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Into the dialog, not at the first control: a form field taking focus
    // means a stray keystroke edits something before the reader has seen what
    // they opened.
    const t = setTimeout(() => { if (panel.current) panel.current.focus(); }, 0);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = had;
      clearTimeout(t);
      const back = returnTo.current;
      if (back && typeof back.focus === 'function') back.focus();
    };
  }, [close]);

  return (
    <div
      className="md-backdrop"
      onMouseDown={(e) => { backdropPress.current = e.target === e.currentTarget; }}
      onMouseUp={(e) => {
        if (backdropPress.current && e.target === e.currentTarget) close();
        backdropPress.current = false;
      }}
    >
      <div
        className={`md-panel${wide ? ' md-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
      >
        <div className="md-head">
          <div className="md-titles">
            <h2 className="md-title">{title}</h2>
            {subtitle && <p className="md-subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="md-close" onClick={close} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="md-body">{children}</div>
        {footer && <div className="md-foot">{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;
